import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useThemeColor } from "@/hooks/useThemeColor";
import { isDomainAllowed, normalizeUrl } from "@/utils/url";
import { ThemedText } from "@/components/ThemedText";
import { IconSymbol } from "./IconSymbol";
import CustomModal from "./CustomModal.web";
import { Button } from "./Button";

type Props = {
  sourceUrl: string;
  setSourceUrl: (value: string) => void;
};

export function SourceInput({ sourceUrl, setSourceUrl }: Props) {
  const { t } = useTranslation();
  const color = useThemeColor({}, "text");
  const backgroundColor = useThemeColor({}, "background");
  const tintColor = useThemeColor({}, "tint");
  const iconColor = useThemeColor({}, "icon");

  const [visible, setVisible] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const openModal = () => {
    setDraft(sourceUrl);
    setError(null);
    setVisible(true);
  };

  const handleSave = () => {
    const normalized = normalizeUrl(draft);
    if (normalized === null) {
      setError(t("VALIDATION_INVALID_URL"));
      return;
    }
    if (normalized !== "" && !isDomainAllowed(normalized)) {
      setError(t("SOURCE_URL_DOMAIN_NOT_ALLOWED"));
      return;
    }
    setSourceUrl(normalized);
    setVisible(false);
  };

  return (
    <>
      <button
        data-testid="source-button"
        style={{ background: "none", border: "none", cursor: "pointer" }}
        onClick={openModal}
        aria-label={t("ADD_SOURCE")}
      >
        <IconSymbol
          name="link"
          color={sourceUrl ? "#b50000ff" : tintColor}
          style={{ marginLeft: 8 }}
        />
      </button>

      <CustomModal
        visible={visible}
        onClose={() => setVisible(false)}
        title={t("SOURCE_MODAL_TITLE")}
      >
        <ThemedText
          style={{ fontSize: 13, color: iconColor, marginBottom: 12 }}
        >
          {t("SOURCE_MODAL_HELPER")}
        </ThemedText>
        <input
          type="url"
          data-testid="source-url-input"
          inputMode="url"
          placeholder={t("SOURCE_URL_PLACEHOLDER")}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) setError(null);
          }}
          maxLength={2048}
          autoFocus
          style={{
            width: "100%",
            color,
            backgroundColor,
            border: `1px solid ${iconColor}`,
            borderRadius: 6,
            outline: "none",
            caretColor: tintColor,
            fontFamily: "inherit",
            fontSize: 14,
            padding: "8px 10px",
            boxSizing: "border-box",
          }}
        />
        {error && (
          <ThemedText
            testID="source-error"
            style={{ color: "#e11d48", fontSize: 12, marginTop: 6 }}
          >
            {error}
          </ThemedText>
        )}
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            marginTop: 16,
            flexWrap: "wrap",
          }}
        >
          {sourceUrl && (
            <Button
              text={t("SOURCE_URL_REMOVE")}
              onPress={() => {
                setSourceUrl("");
                setVisible(false);
              }}
              style={{ backgroundColor: "gray", marginRight: "auto" }}
            />
          )}
          <Button
            text={t("CANCEL")}
            onPress={() => setVisible(false)}
            style={{ backgroundColor: "gray" }}
          />
          <Button
            testID="source-save-button"
            text={t("SAVE")}
            onPress={handleSave}
          />
        </div>
      </CustomModal>
    </>
  );
}
